/**
 * Editing Toolbar Component
 *
 * Toolbar for geometry editing operations including mode selection,
 * undo/redo, and access to various editing tools.
 */

import { useCallback } from 'react';
import { useGeometryEditStore, useEditMode, useIsEditing, useUndoRedo, useSelection } from '../../geometryEditStore';

/**
 * Props for EditingToolbar.
 */
interface EditingToolbarProps {
  onOpenZoneWizard?: () => void;
  onOpenFloorPlan?: () => void;
  onOpenAdjacencyDetector?: () => void;
  onOpenConstructionBuilder?: () => void;
}

/**
 * Tool button component.
 */
function ToolButton({
  icon,
  label,
  active = false,
  disabled = false,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick?: (() => void) | undefined;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-col items-center rounded px-3 py-1.5 text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 focus:ring-offset-background ${
        active
          ? 'bg-primary/10 text-primary dark:bg-primary/20'
          : disabled
            ? 'cursor-not-allowed text-muted-foreground/50'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
      title={label}
      aria-label={label}
      aria-pressed={active}
    >
      <div className="mb-0.5">{icon}</div>
      <span>{label}</span>
    </button>
  );
}

/**
 * Editing Toolbar Component.
 */
export function EditingToolbar({
  onOpenZoneWizard,
  onOpenFloorPlan,
  onOpenAdjacencyDetector,
  onOpenConstructionBuilder,
}: EditingToolbarProps) {
  const editMode = useEditMode();
  const isEditing = useIsEditing();
  const selection = useSelection();
  const { canUndo, canRedo, undo, redo } = useUndoRedo();

  const enableEditing = useGeometryEditStore((s) => s.enableEditing);
  const disableEditing = useGeometryEditStore((s) => s.disableEditing);
  const setEditMode = useGeometryEditStore((s) => s.setEditMode);
  const openZoneWizard = useGeometryEditStore((s) => s.openZoneWizard);
  const openFenestrationPlacer = useGeometryEditStore((s) => s.openFenestrationPlacer);
  const openConstructionBuilder = useGeometryEditStore((s) => s.openConstructionBuilder);
  const clearSelection = useGeometryEditStore((s) => s.clearSelection);
  const deleteSurface = useGeometryEditStore((s) => s.deleteSurface);
  const flipSurfaceNormal = useGeometryEditStore((s) => s.flipSurfaceNormal);

  const handleToggleEditing = useCallback(() => {
    if (isEditing) {
      disableEditing();
    } else {
      enableEditing();
    }
  }, [isEditing, enableEditing, disableEditing]);

  const handleOpenZoneWizard = useCallback(() => {
    openZoneWizard();
    onOpenZoneWizard?.();
  }, [openZoneWizard, onOpenZoneWizard]);

  const handleDeleteSelection = useCallback(() => {
    for (const surfaceId of selection.surfaceIds) {
      deleteSurface(surfaceId);
    }
    clearSelection();
  }, [selection.surfaceIds, deleteSurface, clearSelection]);

  const handleFlipNormals = useCallback(() => {
    for (const surfaceId of selection.surfaceIds) {
      flipSurfaceNormal(surfaceId);
    }
  }, [selection.surfaceIds, flipSurfaceNormal]);

  const handlePlaceFenestration = useCallback(() => {
    if (selection.surfaceIds.length === 1 && selection.surfaceIds[0]) {
      openFenestrationPlacer(selection.surfaceIds[0]);
    }
  }, [selection.surfaceIds, openFenestrationPlacer]);

  const hasSelection = selection.surfaceIds.length > 0;
  const hasSingleSurfaceSelected = selection.surfaceIds.length === 1;

  return (
    <div className="flex items-center justify-between border-b border-border bg-card px-4 py-2">
      {/* Left section - Mode toggle */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleToggleEditing}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background ${
            isEditing
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'bg-muted text-foreground hover:bg-muted/80'
          }`}
          aria-pressed={isEditing}
        >
          {isEditing ? 'Exit Edit Mode' : 'Edit Geometry'}
        </button>

        {isEditing && (
          <div className="flex items-center gap-1 border-l border-border pl-4">
            {/* Select tool */}
            <ToolButton
              icon={
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
              }
              label="Select"
              active={editMode === 'select'}
              onClick={() => setEditMode('select')}
            />

            {/* Zone tools */}
            <ToolButton
              icon={
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              }
              label="Add Zone"
              onClick={handleOpenZoneWizard}
            />

            {/* 2D Plan */}
            <ToolButton
              icon={
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
              }
              label="2D Plan"
              onClick={onOpenFloorPlan}
            />

            {/* Surface edit */}
            <ToolButton
              icon={
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              }
              label="Edit"
              active={editMode === 'surface-edit'}
              onClick={() => setEditMode('surface-edit')}
            />

            {/* Window placement */}
            <ToolButton
              icon={
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                </svg>
              }
              label="Window"
              disabled={!hasSingleSurfaceSelected}
              onClick={handlePlaceFenestration}
            />

            {/* Adjacency detection */}
            <ToolButton
              icon={
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              }
              label="Adjacency"
              onClick={onOpenAdjacencyDetector}
            />

            {/* Constructions */}
            <ToolButton
              icon={
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              }
              label="Layers"
              onClick={() => {
                openConstructionBuilder();
                onOpenConstructionBuilder?.();
              }}
            />
          </div>
        )}
      </div>

      {/* Center section - Selection actions */}
      {isEditing && hasSelection && (
        <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-1.5" role="group" aria-label="Selection actions">
          <span className="text-sm text-muted-foreground" role="status" aria-live="polite">
            {selection.surfaceIds.length} surface{selection.surfaceIds.length > 1 ? 's' : ''} selected
          </span>
          <div className="mx-2 h-4 w-px bg-border" aria-hidden="true" />
          <button
            onClick={handleFlipNormals}
            className="rounded bg-secondary px-2 py-1 text-xs text-secondary-foreground hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
            title="Flip surface normals"
            aria-label="Flip normals of selected surfaces"
          >
            Flip Normal
          </button>
          <button
            onClick={handleDeleteSelection}
            className="rounded bg-destructive/10 px-2 py-1 text-xs text-destructive hover:bg-destructive/20 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-1"
            title="Delete selected surfaces"
            aria-label="Delete selected surfaces"
          >
            Delete
          </button>
        </div>
      )}

      {/* Right section - Undo/Redo */}
      {isEditing && (
        <div className="flex items-center gap-2" role="group" aria-label="History controls">
          <button
            onClick={() => undo()}
            disabled={!canUndo}
            className="rounded p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 focus:ring-offset-background"
            title="Undo (Ctrl+Z)"
            aria-label="Undo last action"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </button>
          <button
            onClick={() => redo()}
            disabled={!canRedo}
            className="rounded p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 focus:ring-offset-background"
            title="Redo (Ctrl+Y)"
            aria-label="Redo last undone action"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

export default EditingToolbar;
